// iter150-review — Android-style bottom navigation. 5 tabs:
//   School users:  Home · Daily · Reports · Inbox · More
//   Admin users:   Admin · Schools · CRM · Inbox · More
//
// Admin tabs all route to /admin so clicking any one of them opens the
// Admin Dashboard on the right tab:
//   Admin   → /admin           (default — admin overview / schools)
//   Schools → /admin           (schools tab)
//   CRM     → /admin?tab=crm
//   Inbox   → /admin?tab=inbox
// Active state is driven by `?tab=` (not the pathname) so Admin and
// Schools can't both light up at the same time. See `tabActive()`.
//
// Inbox badge uses the shared `useUnreadCount()` hook — role-aware,
// visibility + online gated, 30 s poll. Role filtering happens on the
// backend's `/api/inbox/unread-count`: admin gets totalUnread across
// all threads (unreadAdmin), schools get their own thread count
// (unreadSchool). They are NEVER summed together.
import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, ClipboardList, FileBarChart, LayoutGrid, Send, Grid3x3, Users as UsersIcon, Briefcase } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import MoreSheet from "@/components/mobile/MoreSheet";
import { useUnreadCount } from "@/lib/useUnreadCount";

const SCHOOL_TABS = [
  { to: "/dashboard", label: "Home",    Icon: Home,          testid: "bottom-nav-home" },
  { to: "/daily",     label: "Daily",   Icon: ClipboardList, testid: "bottom-nav-daily" },
  { to: "/reports",   label: "Reports", Icon: FileBarChart,  testid: "bottom-nav-reports" },
  { to: "/inbox",     label: "Inbox",   Icon: Send,          testid: "bottom-nav-inbox", unread: true },
];

// Admin bottom-nav items. Each has an `activeWhen(path, tab)` predicate
// so the highlight is driven by the current ?tab param, not pathname.
const ADMIN_TABS = [
  {
    to: "/admin",
    label: "Admin",
    Icon: LayoutGrid,
    testid: "bottom-nav-admin",
    // Admin lights up on /admin for any tab NOT in this bar.
    activeWhen: (path, tab) =>
      path.startsWith("/admin") && !path.startsWith("/admin/") &&
      ["messages", "reviews", "plans", "subscriptions", "settings", "rateCards"].includes(tab),
  },
  {
    to: "/admin",
    label: "Schools",
    Icon: UsersIcon,
    testid: "bottom-nav-schools",
    // Schools lights up on /admin with empty tab or ?tab=schools.
    activeWhen: (path, tab) =>
      path.startsWith("/admin") && !path.startsWith("/admin/") &&
      (!tab || tab === "schools"),
  },
  {
    to: "/admin?tab=crm",
    label: "CRM",
    Icon: Briefcase,
    testid: "bottom-nav-crm",
    activeWhen: (path, tab) => path.startsWith("/admin") && tab === "crm",
  },
  {
    to: "/admin?tab=inbox",
    label: "Inbox",
    Icon: Send,
    testid: "bottom-nav-inbox",
    unread: true,
    activeWhen: (path, tab) => path.startsWith("/admin") && tab === "inbox",
  },
];

export default function BottomNav() {
  const { user } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const unread = useUnreadCount();
  const location = useLocation();
  if (!user) return null;
  const isAdmin = user.role === "admin";
  const tabs = isAdmin ? ADMIN_TABS : SCHOOL_TABS;
  const currentTab = new URLSearchParams(location.search).get("tab") || "";

  return (
    <>
      <nav
        data-testid="bottom-nav"
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 items-stretch border-t border-slate-200 bg-white/90 pb-[max(env(safe-area-inset-bottom),0.25rem)] pt-1 shadow-[0_-2px_18px_-6px_rgba(15,23,42,0.18)] backdrop-blur-xl md:hidden"
        aria-label="Primary mobile navigation"
      >
        {tabs.map(({ to, label, Icon, testid, unread: wantsUnread, activeWhen }) => {
          // For admin tabs, we drive the active state from the URL
          // ?tab param so Admin and Schools never both light up.
          // For school tabs we fall back to NavLink's default pathname
          // matching (via `end`).
          const forcedActive = isAdmin && activeWhen
            ? activeWhen(location.pathname, currentTab)
            : null;
          return (
            <NavLink
              key={to + label}
              to={to}
              end={to === "/"}
              data-testid={testid}
              className={({ isActive }) => {
                const active = forcedActive !== null ? forcedActive : isActive;
                return `relative flex min-h-[52px] flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-semibold transition-colors active:scale-95 ${
                  active ? "text-brand-600" : "text-slate-500 hover:text-brand-600"
                }`;
              }}
            >
              {({ isActive }) => {
                const active = forcedActive !== null ? forcedActive : isActive;
                return (
                  <>
                    <span
                      className={`relative flex h-7 w-12 items-center justify-center rounded-full transition-colors ${
                        active ? "bg-brand-50 text-brand-600" : "text-slate-500"
                      }`}
                    >
                      <Icon className="h-4 w-4" strokeWidth={2.25} />
                      {wantsUnread && unread > 0 && (
                        <span
                          data-testid="bottom-nav-inbox-badge"
                          className="absolute -top-1 right-1 grid h-4 min-w-[1rem] place-items-center rounded-full bg-rose-500 px-1 text-[0.6rem] font-bold text-white shadow-sm"
                          aria-label={`${unread} unread`}
                        >
                          {unread > 9 ? "9+" : unread}
                        </span>
                      )}
                    </span>
                    <span className="leading-tight">{label}</span>
                  </>
                );
              }}
            </NavLink>
          );
        })}
        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          data-testid="bottom-nav-more"
          className={`relative flex min-h-[52px] flex-col items-center justify-center gap-0.5 px-1 text-[11px] font-semibold transition-colors active:scale-95 ${
            moreOpen ? "text-brand-600" : "text-slate-500 hover:text-brand-600"
          }`}
        >
          <span
            className={`flex h-7 w-12 items-center justify-center rounded-full transition-colors ${
              moreOpen ? "bg-brand-50 text-brand-600" : "text-slate-500"
            }`}
          >
            <Grid3x3 className="h-4 w-4" strokeWidth={2.25} />
          </span>
          <span className="leading-tight">More</span>
        </button>
      </nav>
      <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </>
  );
}
